import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  LayoutList,
  RefreshCcw,
  Rows3,
  Search,
  SearchX,
  type LucideIcon,
} from "lucide-react";
import type { TripScheduleItem } from "@mtform/shared";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { DateField } from "@/components/form/DateField";
import {
  formatIsoDateForDisplay,
  normalizeDateForPayload,
  toIsoDate,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import { useTodayIso } from "@/hooks/useTodayIso";
import { SelectionPill } from "@/components/ui/selection-pill";
import { TripCard } from "./TripCard";
import { TripCardSkeleton } from "./TripCardSkeleton";
import { matchesSearch } from "./search";

// =============================================================================
// BookedTripsView
//
// Single page that lists every trip dispatch has on the books for a given
// date. A toolbar picks the date and searches (trip ID, name, MRN,
// requester); the status tabs under it carry each group's count and jump
// straight to one group — "show me only the will-call trips that need to be
// dialed in" without scrolling past everything else.
// =============================================================================

const COMPLETED_STATUSES = new Set(["finished", "no_show"]);
const CANCELED_STATUSES = new Set([
  "customer_canceled",
  "customer_requested_cancel",
  "organization_canceled",
  "fleet_canceled",
  "late_cancel",
  "deleted",
]);
const WILL_CALL_STATUSES = new Set(["will_call"]);

const COMPACT_KEY = "rt_sinai_schedule_compact_v2";
const REFRESH_INTERVAL_MS = 60 * 1000;

type GroupKey = "active" | "willCall" | "completed" | "canceled";
type FilterKey = "all" | GroupKey;

export function BookedTripsView() {
  // Follows the clock, so a page left open past midnight moves "today"
  // along (Jump to today used to go back to the day the page opened).
  const today = formatIsoDateForDisplay(useTodayIso());
  // A search handed over by the command menu (see the effect below) is
  // applied on the very first render, so the whole day never flashes up
  // before it narrows.
  const location = useLocation();
  const handover = location.state as { search?: string; date?: string } | null;
  const [dateInput, setDateInput] = useState(() =>
    handover?.date ? formatIsoDateForDisplay(handover.date) : today,
  );
  const [search, setSearch] = useState(() => handover?.search ?? "");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [compact, setCompact] = useState(() => {
    try {
      return localStorage.getItem(COMPACT_KEY) === "1";
    } catch {
      return false;
    }
  });

  const isoDate = normalizeDateForPayload(dateInput);

  const [tabsEl, setTabsEl] = useState<HTMLDivElement | null>(null);

  const queryClient = useQueryClient();
  const scheduleQuery = useQuery({
    queryKey: ["schedule", isoDate],
    queryFn: ({ signal }) => api.trip.schedule({ date: isoDate }, signal),
    enabled: Boolean(isoDate),
    staleTime: 30 * 1000,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  // The command menu hands over a trip search (and its day) in the
  // navigation state rather than the URL, so patient names and MRNs stay
  // out of the address bar and history. Applied once, then cleared.
  const navigate = useNavigate();
  useEffect(() => {
    const seed = location.state as { search?: string; date?: string } | null;
    if (!seed || (seed.search == null && seed.date == null)) return;
    if (seed.date) setDateInput(formatIsoDateForDisplay(seed.date));
    if (seed.search != null) setSearch(seed.search);
    setFilter("all");
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // How the list arrives: a new day slides in from the side you moved
  // toward; a new status filter, or a day's trips replacing the loading
  // skeletons of a load the user started, fades in. Played with the Web
  // Animations API on the list element that is already there, so nothing
  // remounts (a card mid-Activate keeps its disabled state, opened rows stay
  // open) and nothing lingers once it ends (no fill, so no containing block
  // is left behind). A half-typed date is skipped entirely, so nothing moves
  // while typing it. Background refetches never animate: a loaded day's
  // refresh doesn't flip isLoading, and a failed day's minute retry (which
  // does, in TanStack v5) isn't a user load. The 12px slide stays inside
  // <main>'s padding at every width.
  const listRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef(false);
  const loading = scheduleQuery.isLoading;
  const shownView = useRef({ iso: isoDate, filter, loading, userLoad: loading });
  useLayoutEffect(() => {
    if (!isoDate) return;
    const prev = shownView.current;
    const dayOrFilter = prev.iso !== isoDate || prev.filter !== filter;
    // A load counts as the user's when it starts with a day or filter switch,
    // the retry button or the first visit — also when a switch lands while a
    // background refetch is still loading. One that starts on its own is a
    // refetch.
    const userLoad =
      loading && (dayOrFilter || retryRef.current || (prev.loading && prev.userLoad));
    if (loading) retryRef.current = false;
    const arrived = prev.loading && !loading && prev.userLoad;
    shownView.current = { iso: isoDate, filter, loading, userLoad };
    if (!dayOrFilter && !arrived) return;
    const el = listRef.current;
    if (!el || typeof el.animate !== "function") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const dx = prev.iso !== isoDate && prev.iso ? (isoDate > prev.iso ? 12 : -12) : 0;
    el.animate(
      [
        { opacity: 0, transform: `translate3d(${dx}px, 0, 0)` },
        { opacity: 1, transform: "translate3d(0, 0, 0)" },
      ],
      { duration: dx ? 300 : 220, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  }, [isoDate, filter, loading]);

  function setCompactMode(next: boolean) {
    setCompact(next);
    try {
      localStorage.setItem(COMPACT_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  }

  function shiftDay(delta: number) {
    if (!isoDate) return;
    const [y, m, d] = isoDate.split("-").map(Number);
    const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
    dt.setDate(dt.getDate() + delta);
    setDateInput(formatIsoDateForDisplay(toIsoDate(dt)));
  }

  const allTrips = scheduleQuery.data?.trips ?? [];
  const filteredBySearch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allTrips;
    return allTrips.filter((t) => matchesSearch(t, q));
  }, [allTrips, search]);

  const groups = useMemo(() => partition(filteredBySearch), [filteredBySearch]);
  const counts = useMemo(
    () => ({
      total: filteredBySearch.length,
      active: groups.active.length,
      willCall: groups.willCall.length,
      completed: groups.completed.length,
      canceled: groups.canceled.length,
    }),
    [filteredBySearch.length, groups],
  );

  // Build round-trip pairings from the *unfiltered* trip set so a
  // search that hides one leg still tells the visible leg "this is
  // part of a round trip" (and which other friendlyId carries the
  // other leg). Round-trips share a friendlyId base — `ABC123-A` and
  // `ABC123-B` are the same group; one-way trips end up as singletons
  // and stay out of the map.
  const pairings = useMemo(() => buildPairings(allTrips), [allTrips]);

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-5">
      {/* Header — same type as the trip request form's "New trip request".
          The Detailed/Compact toggle sits on the right. */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-[2.5rem] sm:leading-[1.1]">
            Booked trips
          </h1>
          <p className="mt-2 text-[15px] text-muted-foreground">
            Every trip on the books for the selected date.
          </p>
        </div>
        <ViewModeToggle compact={compact} onChange={setCompactMode} />
      </div>

      {/* Toolbar — date stepper, search and the live-refresh caption on one
          frosted bar. The field labels stay for screen readers; visually the
          arrows, the calendar and the magnifier say what the controls do. */}
      <div className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-card/60 p-3 shadow-[0_24px_60px_-40px_hsl(222_90%_2%/0.9)] backdrop-blur-xl lg:flex-row lg:items-center">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="bt-date" className="sr-only">
            Trip date
          </label>
          <div className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="icon"
              type="button"
              aria-label="Previous day"
              onClick={() => shiftDay(-1)}
              className="shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="w-full max-w-[12.5rem]">
              <DateField id="bt-date" value={dateInput} onChange={setDateInput} clearable={false} />
            </div>
            <Button
              variant="secondary"
              size="icon"
              type="button"
              aria-label="Next day"
              onClick={() => shiftDay(1)}
              className="shrink-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          {dateInput !== today ? (
            <button
              type="button"
              onClick={() => setDateInput(today)}
              className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-primary/40 bg-primary/10 px-3 text-xs font-semibold text-primary transition hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CalendarClock className="h-3.5 w-3.5" /> Jump to today
            </button>
          ) : null}
        </div>
        <div className="relative min-w-0 flex-1">
          <label htmlFor="bt-search" className="sr-only">
            Search
          </label>
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id="bt-search"
            type="search"
            placeholder="Search MRN, name, trip ID, requester"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9"
          />
        </div>
        {dateInput === today ? (
          <RefreshIndicator
            isFetching={scheduleQuery.isFetching}
            dataUpdatedAt={scheduleQuery.dataUpdatedAt}
            hasData={Boolean(scheduleQuery.data)}
            refreshIntervalMs={REFRESH_INTERVAL_MS}
          />
        ) : null}
      </div>

      {/* ---- Status tabs ----------------------------------------------------
          All, then one tab per status group with its count. A group tab
          toggles (click it again for All), as the old count pills did. ----- */}
      <div
        ref={setTabsEl}
        role="group"
        aria-label="Filter by status"
        className="relative flex flex-wrap items-center gap-1 justify-self-start rounded-2xl border border-border/60 bg-card/40 p-1 backdrop-blur-xl"
      >
        <SelectionPill container={tabsEl} selected={filter} />
        <StatusTab
          label="All"
          count={counts.total}
          loading={scheduleQuery.isLoading}
          isCurrent={filter === "all"}
          onClick={() => setFilter("all")}
        />
        <StatusTab
          label="Active"
          tone="emerald"
          count={counts.active}
          loading={scheduleQuery.isLoading}
          isCurrent={filter === "active"}
          onClick={() => setFilter(filter === "active" ? "all" : "active")}
        />
        <StatusTab
          label="Will Call"
          tone="sky"
          count={counts.willCall}
          loading={scheduleQuery.isLoading}
          isCurrent={filter === "willCall"}
          onClick={() => setFilter(filter === "willCall" ? "all" : "willCall")}
        />
        <StatusTab
          label="Completed"
          tone="slate"
          count={counts.completed}
          loading={scheduleQuery.isLoading}
          isCurrent={filter === "completed"}
          onClick={() => setFilter(filter === "completed" ? "all" : "completed")}
        />
        <StatusTab
          label="Canceled"
          tone="rose"
          count={counts.canceled}
          loading={scheduleQuery.isLoading}
          isCurrent={filter === "canceled"}
          onClick={() => setFilter(filter === "canceled" ? "all" : "canceled")}
        />
      </div>

      {/* ---- The list. No internal scroll container — we let the page
              scroll naturally so dispatch on a small laptop sees every
              card without an inner scrollbar getting in the way. -------- */}
      <div
        ref={listRef}
        className={cn("flex w-full flex-col", compact ? "gap-2" : "gap-3")}
      >
        {scheduleQuery.isLoading ? (
          // Skeleton cards instead of a spinner — same footprint as
          // the real cards so the layout doesn't jump when data
          // lands, plus a "Loading…" sr-only announcement so AT
          // users still hear the state.
          <>
            <span className="sr-only" role="status">
              Loading trips…
            </span>
            {Array.from({ length: 4 }).map((_, i) => (
              <TripCardSkeleton key={i} compact={compact} />
            ))}
          </>
        ) : scheduleQuery.isError ? (
          <div className="flex flex-col items-center rounded-2xl border border-dashed border-border bg-foreground/[0.02] px-4 py-10">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                retryRef.current = true;
                void queryClient.invalidateQueries({ queryKey: ["schedule", isoDate] });
              }}
            >
              <RefreshCcw className="h-3.5 w-3.5" /> Failed to load — retry
            </Button>
          </div>
        ) : counts.total === 0 ? (
          <EmptyState search={search.trim()} />
        ) : (
          <>
            {(filter === "all" || filter === "active") && groups.active.length > 0 ? (
              <ScheduleGroup
                title="Active"
                tone="emerald"
                trips={groups.active}
                pairings={pairings}
                query={search}
                compact={compact}
              />
            ) : null}
            {(filter === "all" || filter === "willCall") && groups.willCall.length > 0 ? (
              <ScheduleGroup
                title="Will Call"
                tone="sky"
                trips={groups.willCall}
                pairings={pairings}
                query={search}
                compact={compact}
              />
            ) : null}
            {(filter === "all" || filter === "completed") && groups.completed.length > 0 ? (
              <ScheduleGroup
                title="Completed"
                tone="slate"
                trips={groups.completed}
                pairings={pairings}
                query={search}
                compact={compact}
                dimmed
              />
            ) : null}
            {(filter === "all" || filter === "canceled") && groups.canceled.length > 0 ? (
              <ScheduleGroup
                title="Canceled"
                tone="rose"
                trips={groups.canceled}
                pairings={pairings}
                query={search}
                compact={compact}
                dimmed
              />
            ) : null}
            {filter !== "all" && counts[filter] === 0 ? (
              <EmptyState search={search.trim()} filter={filter} />
            ) : null}
          </>
        )}
      </div>

    </section>
  );
}

// =============================================================================
// Subcomponents
// =============================================================================

type Tone = "emerald" | "sky" | "slate" | "rose";

const TONE_CLASSES: Record<Tone, { bg: string; ring: string; text: string; iconText: string }> = {
  emerald: {
    bg: "bg-emerald-400/10",
    ring: "ring-emerald-400/40",
    text: "text-emerald-700 dark:text-emerald-300",
    iconText: "text-emerald-700 dark:text-emerald-400",
  },
  sky: {
    bg: "bg-sky-400/10",
    ring: "ring-sky-400/40",
    text: "text-sky-700 dark:text-sky-300",
    iconText: "text-sky-700 dark:text-sky-400",
  },
  slate: {
    bg: "bg-slate-400/10",
    ring: "ring-slate-400/40",
    text: "text-slate-600 dark:text-slate-300",
    iconText: "text-slate-600 dark:text-slate-400",
  },
  rose: {
    bg: "bg-rose-400/10",
    ring: "ring-rose-400/40",
    text: "text-rose-700 dark:text-rose-300",
    iconText: "text-rose-700 dark:text-rose-400",
  },
};

/** One status tab: coloured dot, label, count. The count sits in its own
 *  element so the number reads (and is found) on its own. */
function StatusTab({
  label,
  count,
  tone,
  loading,
  isCurrent,
  onClick,
}: {
  label: string;
  count: number;
  tone?: Tone;
  loading?: boolean;
  isCurrent: boolean;
  onClick: () => void;
}) {
  const tones = tone ? TONE_CLASSES[tone] : null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isCurrent}
      className={cn(
        "relative z-10 inline-flex h-9 items-center gap-2 rounded-xl px-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isCurrent
          ? "text-foreground"
          : "text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground",
      )}
    >
      {tones ? (
        <span aria-hidden="true" className={cn("h-2 w-2 rounded-full bg-current", tones.iconText)} />
      ) : null}
      <span>{label}</span>
      <span
        className={cn(
          "min-w-[1.5rem] rounded-full px-1.5 py-0.5 text-center text-xs tabular-nums",
          isCurrent && tones ? cn(tones.bg, tones.text) : "bg-foreground/[0.06] text-muted-foreground",
        )}
      >
        {loading ? <Spinner className="mx-auto h-3 w-3" /> : count.toLocaleString()}
      </span>
    </button>
  );
}

// =============================================================================
// Live-refresh indicator — small subtitle under the Trip Date control
// that surfaces the auto-refresh behavior (every REFRESH_INTERVAL_MS).
// Dispatch never had to wonder "is this stale?" because the page just
// re-fetched silently; now they get a "Updated 12s ago · auto-refresh
// every 1 min" line and a Refreshing… state during the round-trip.
//
// Re-renders once per second via a setInterval so the relative
// timestamp stays accurate without the whole view re-mounting.
// =============================================================================

function RefreshIndicator({
  isFetching,
  dataUpdatedAt,
  hasData,
  refreshIntervalMs,
}: {
  isFetching: boolean;
  dataUpdatedAt: number;
  hasData: boolean;
  refreshIntervalMs: number;
}) {
  const visible = hasData || isFetching;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Only run the 1 Hz ticker when the caption is actually visible.
    // Without this gate the interval keeps firing on idle background
    // tabs and whenever the parent hides this component (e.g. when
    // the user jumps to a non-today date and the Jump-to-Today pill
    // replaces this caption), burning CPU for no UI effect.
    if (!visible) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [visible]);

  if (!visible) return null;

  const intervalLabel =
    refreshIntervalMs >= 60_000
      ? `${Math.round(refreshIntervalMs / 60_000)} min`
      : `${Math.round(refreshIntervalMs / 1000)} s`;

  return (
    // Deliberately NOT aria-live: the caption re-renders at 1 Hz, so a
    // live region would make screen readers announce "Updated 12s ago"
    // every second for the whole shift.
    <p className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-border/70 bg-foreground/[0.03] px-3 py-1.5 text-xs text-muted-foreground lg:self-auto">
      <RefreshCcw
        className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
      />
      <span>
        {isFetching ? (
          "Refreshing…"
        ) : (
          <>
            Updated {formatRelativeAgo(now - dataUpdatedAt)} · auto-refresh
            every {intervalLabel}
          </>
        )}
      </span>
    </p>
  );
}

/** Tiny humaniser for the "Updated X ago" suffix. Stays terse so the
 *  caption doesn't crowd the date picker on narrow viewports. */
function formatRelativeAgo(ms: number): string {
  if (ms < 5_000) return "just now";
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 60 * 60_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / (60 * 60_000))}h ago`;
}

// =============================================================================
// View-mode toggle — segmented two-button control. Replaced the
// standalone "Compact view" button that used to sit on its own row
// below the stats; now it lives at the right edge of the stats row,
// alongside the other list controls. Icon-only on small viewports
// (where the stack already telegraphs the mode), keeps the label
// on wider screens so the affordance reads clearly.
// =============================================================================

function ViewModeToggle({
  compact,
  onChange,
}: {
  compact: boolean;
  onChange: (next: boolean) => void;
}) {
  const [groupEl, setGroupEl] = useState<HTMLDivElement | null>(null);
  return (
    <div
      ref={setGroupEl}
      role="group"
      aria-label="List view mode"
      className="relative inline-flex shrink-0 items-center self-start rounded-xl border border-border/70 bg-card/60 p-1 backdrop-blur-xl sm:self-auto"
    >
      <SelectionPill
        container={groupEl}
        selected={compact ? "compact" : "detailed"}
        rounded="rounded-lg"
      />
      <ViewModeButton
        icon={LayoutList}
        label="Detailed"
        active={!compact}
        onClick={() => onChange(false)}
      />
      <ViewModeButton
        icon={Rows3}
        label="Compact"
        active={compact}
        onClick={() => onChange(true)}
      />
    </div>
  );
}

function ViewModeButton({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      title={`${label} view`}
      className={cn(
        "relative z-10 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? "text-foreground"
          : "text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function EmptyState({ search, filter }: { search: string; filter?: FilterKey }) {
  const Icon = search ? SearchX : CalendarDays;
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-foreground/[0.02] px-4 py-12 text-center">
      <span
        aria-hidden="true"
        className="flex h-11 w-11 items-center justify-center rounded-full bg-foreground/[0.05] text-muted-foreground"
      >
        <Icon className="h-5 w-5" />
      </span>
      {search ? (
        <p className="text-sm text-muted-foreground">
          No trips match <strong className="font-bold text-foreground">"{search}"</strong>.
        </p>
      ) : filter && filter !== "all" ? (
        <p className="text-sm text-muted-foreground">No {filterLabel(filter)} trips for this date.</p>
      ) : (
        <p className="text-sm text-muted-foreground">No trips booked for this date.</p>
      )}
    </div>
  );
}

function ScheduleGroup({
  title,
  tone,
  trips,
  pairings,
  query,
  compact,
  dimmed,
}: {
  title: string;
  tone: Tone;
  trips: TripScheduleItem[];
  pairings: Map<string, TripPairing>;
  query: string;
  compact: boolean;
  dimmed?: boolean;
}) {
  const sorted = useMemo(() => sortByPickup(trips), [trips]);
  const tones = TONE_CLASSES[tone];
  return (
    <section className={cn("flex flex-col pt-3 first:pt-0", compact ? "gap-1.5" : "gap-2")}>
      {/* Sticks under the 64px workspace bar while the group scrolls. */}
      <div className="sticky top-16 z-10 flex items-center justify-between gap-2 rounded-xl border border-border/60 bg-background/80 px-3 py-2 backdrop-blur-xl">
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em]">
          <span className={cn("h-2 w-2 rounded-full", tones.iconText, "bg-current")} />
          <span className={tones.text}>{title}</span>
        </span>
        <span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[0.7rem] font-semibold tabular-nums text-muted-foreground">
          {trips.length}
        </span>
      </div>
      {sorted.map((trip) => (
        <TripCard
          key={trip.friendlyId}
          trip={trip}
          query={query}
          compact={compact}
          dimmed={Boolean(dimmed)}
          pairing={pairings.get(String(trip.friendlyId)) ?? null}
          accent={tone}
        />
      ))}
    </section>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/** A friendlyId's round-trip sibling info. Singletons (one-way trips)
 *  never make it into the map this builds — only trips that share a
 *  base ID with at least one sibling.
 *
 *  Sibling entries carry a `label` ("Outbound" / "Return" / "Leg X")
 *  derived from the suffix letter so the UI can say e.g. "Return:
 *  293-B [WILL CALL]" instead of the abstract "Leg 2 of 2". */
export interface TripPairing {
  /** Total legs in the round-trip (2 for typical home → appt → home). */
  totalLegs: number;
  siblings: Array<{ friendlyId: string; status: string; label: string }>;
}

/** Split friendlyId on the last `-` and group by everything-before.
 *  `260500000293-A` and `260500000293-B` → same group, base = the
 *  digits prefix. Trips with no suffix end up as their own group of
 *  one (filtered out below). Matches triplink's `groupId` convention
 *  on /create responses without needing the schedule response to
 *  carry the group explicitly. */
function buildPairings(trips: TripScheduleItem[]): Map<string, TripPairing> {
  const groups = new Map<string, TripScheduleItem[]>();
  for (const t of trips) {
    const id = String(t.friendlyId ?? "");
    if (!id) continue;
    const dash = id.lastIndexOf("-");
    const base = dash > 0 ? id.slice(0, dash) : id;
    const bucket = groups.get(base);
    if (bucket) bucket.push(t);
    else groups.set(base, [t]);
  }

  const out = new Map<string, TripPairing>();
  for (const bucket of groups.values()) {
    if (bucket.length < 2) continue;
    // Sort by suffix letter so `A` is leg 1, `B` is leg 2 — matches
    // dispatch's mental model (outbound, then return).
    const sorted = [...bucket].sort((a, b) => {
      const sa = suffixOf(String(a.friendlyId ?? ""));
      const sb = suffixOf(String(b.friendlyId ?? ""));
      return sa.localeCompare(sb);
    });
    sorted.forEach((trip) => {
      const id = String(trip.friendlyId ?? "");
      out.set(id, {
        totalLegs: sorted.length,
        siblings: sorted
          .filter((s) => String(s.friendlyId) !== id)
          .map((s) => ({
            friendlyId: String(s.friendlyId),
            status: String(s.status ?? "").toLowerCase(),
            label: legLabel(suffixOf(String(s.friendlyId ?? "")), sorted.length),
          })),
      });
    });
  }
  return out;
}

function suffixOf(friendlyId: string): string {
  const dash = friendlyId.lastIndexOf("-");
  return dash >= 0 ? friendlyId.slice(dash + 1) : friendlyId;
}

/** Map a friendlyId suffix to a human-friendly leg name. Two-leg
 *  round trips (the overwhelmingly common case) use Outbound / Return.
 *  Anything weirder (3+ legs, non-letter suffix) falls back to the
 *  letter suffix verbatim so we never silently mislabel. */
function legLabel(suffix: string, totalLegs: number): string {
  const s = suffix.toUpperCase();
  if (totalLegs === 2) {
    if (s === "A" || s === "1") return "Outbound";
    if (s === "B" || s === "2") return "Return";
  }
  return `Leg ${s}`;
}

function partition(trips: TripScheduleItem[]) {
  const active: TripScheduleItem[] = [];
  const willCall: TripScheduleItem[] = [];
  const completed: TripScheduleItem[] = [];
  const canceled: TripScheduleItem[] = [];
  for (const t of trips) {
    const s = String(t.status ?? "").toLowerCase();
    if (CANCELED_STATUSES.has(s)) canceled.push(t);
    else if (COMPLETED_STATUSES.has(s)) completed.push(t);
    else if (WILL_CALL_STATUSES.has(s)) willCall.push(t);
    else active.push(t);
  }
  return { active, willCall, completed, canceled };
}

function sortByPickup(trips: TripScheduleItem[]) {
  return [...trips].sort((a, b) => {
    // Sort on `pickupAtIso` (ISO 8601 UTC) first — that's the only
    // format Date.parse is guaranteed to handle consistently across
    // browsers. Falling back to `pickupTime` (e.g. "May 11, 2026
    // 13:50") would otherwise break ordering on Safari, which
    // returns NaN for that format and dumps every trip into the
    // friendlyId tiebreak.
    const at = parseTripIso(a.pickupAtIso) ?? parseTripIso(a.pickupTime);
    const bt = parseTripIso(b.pickupAtIso) ?? parseTripIso(b.pickupTime);
    if (at !== bt) return at - bt;
    return String(a.friendlyId).localeCompare(String(b.friendlyId));
  });
}

function parseTripIso(value: string | undefined) {
  if (!value) return Number.POSITIVE_INFINITY;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

function filterLabel(filter: FilterKey): string {
  switch (filter) {
    case "active":
      return "active";
    case "willCall":
      return "Will Call";
    case "completed":
      return "completed";
    case "canceled":
      return "canceled";
    default:
      return "";
  }
}
